import api from './api';

class SubcontractingService {
  // Get all subcontracting records with pagination and filtering
  static async getSubcontractings(params = {}) {
    try {
      const response = await api.get('/subcontracting', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching subcontracting records:', error);
      throw this.handleError(error);
    }
  }

  // Get subcontracting record by ID
  static async getSubcontracting(id) {
    try {
      const response = await api.get(`/subcontracting/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching subcontracting record:', error);
      throw this.handleError(error);
    }
  }

  // Map contractor form data to subcontracting schema
  static mapContractorToSubcontracting(contractorData) {
    // Parse address components
    const addressParts = contractorData.address ? contractorData.address.split(',') : [];
    const street = addressParts[0]?.trim() || contractorData.address || '';
    
    // Map business type to legal entity type
    const businessTypeMap = {
      'corporation': 'Corporation',
      'llc': 'LLC',
      'partnership': 'Partnership',
      'sole-proprietorship': 'Sole Proprietorship',
      'non-profit': 'Non-Profit'
    };

    // Map payment terms
    const paymentTermsMap = {
      'net-15': 'Net 15',
      'net-30': 'Net 30',
      'net-45': 'Net 45',
      'net-60': 'Net 60',
      'due-on-receipt': 'Due on Receipt'
    };

    // Map preferred communication
    const communicationMap = {
      'email': 'Email',
      'phone': 'Phone',
      'sms': 'Phone',
      'portal': 'Portal'
    };

    // Helper functions
    const cleanValue = (value) => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? undefined : trimmed;
      }
      return value;
    };

    const formatPhone = (phone) => {
      if (!phone) return undefined;
      const cleaned = phone.trim();
      if (cleaned === '') return undefined;
      
      let digitsOnly = cleaned.replace(/[^\d+]/g, '');
      
      if (digitsOnly.startsWith('+')) {
        if (digitsOnly.length >= 9) {
          return digitsOnly;
        }
      }
      
      if (digitsOnly.length === 10) {
        return `+1${digitsOnly}`;
      } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        return `+${digitsOnly}`;
      } else if (digitsOnly.length >= 10 && digitsOnly.length <= 15) {
        return digitsOnly.startsWith('+') ? digitsOnly : `+${digitsOnly}`;
      }
      
      console.warn('Phone number format may not be valid for validation:', cleaned);
      return undefined;
    };

    const formatWebsite = (website) => {
      if (!website) return undefined;
      const cleaned = website.trim();
      if (cleaned === '') return undefined;
      
      if (!cleaned.match(/^https?:\/\//i)) {
        if (cleaned.match(/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}/)) {
          return `https://${cleaned}`;
        }
        return undefined;
      }
      
      try {
        new URL(cleaned);
        return cleaned;
      } catch (e) {
        return undefined;
      }
    };

    // Map certifications array - handle both strings and objects
    const certifications = (contractorData.certifications || []).map(cert => {
      // If it's a string, create a simple certification object
      if (typeof cert === 'string' && cert.trim()) {
        return {
          name: cert.trim(),
          issuer: '',
          issueDate: null,
          expiryDate: null,
          certificateNumber: ''
        };
      }
      // If it's an object, use its properties
      if (cert && typeof cert === 'object') {
        return {
          name: cert.name || cert || '',
          issuer: cert.issuer || '',
          issueDate: cert.issueDate || null,
          expiryDate: cert.expiryDate || null,
          certificateNumber: cert.certificateNumber || ''
        };
      }
      return null;
    }).filter(cert => cert && cert.name); // Filter out empty certifications

    // Map service categories from specialties and services
    const serviceCategories = [];
    if (contractorData.specialties && contractorData.specialties.length > 0) {
      contractorData.specialties.forEach(specialty => {
        if (specialty && specialty.trim()) {
          serviceCategories.push({
            category: specialty.trim(),
            subcategory: '',
            description: ''
          });
        }
      });
    }
    // Also add services to serviceCategories
    if (contractorData.services && contractorData.services.length > 0) {
      contractorData.services.forEach(service => {
        if (service && service.trim()) {
          // Check if this service is already in serviceCategories (from specialties)
          const exists = serviceCategories.some(sc => sc.category.toLowerCase() === service.trim().toLowerCase());
          if (!exists) {
            serviceCategories.push({
              category: service.trim(),
              subcategory: '',
              description: ''
            });
          }
        }
      });
    }

    // Get phone and website values
    const phoneValue = formatPhone(contractorData.phone);
    const websiteValue = formatWebsite(contractorData.website);
    const emailValue = cleanValue(contractorData.email);

    // Build subcontracting data object
    const subcontractingData = {
      // Basic Information - REQUIRED
      name: contractorData.companyName?.trim() || '',
    };

    // Add optional basic fields only if they have values
    if (contractorData.companyName) {
      subcontractingData.legalName = contractorData.companyName.trim();
      subcontractingData.tradingName = contractorData.companyName.trim();
    }

    if (contractorData.industry) {
      subcontractingData.industry = contractorData.industry.trim();
      subcontractingData.sector = contractorData.industry.trim();
    }
    
    // Business Registration
    const businessReg = {};
    if (contractorData.taxId) businessReg.taxId = contractorData.taxId.trim();
    if (contractorData.registrationNumber) businessReg.registrationNumber = contractorData.registrationNumber.trim();
    if (contractorData.businessType) {
      businessReg.legalEntityType = businessTypeMap[contractorData.businessType] || 'Corporation';
    }
    if (contractorData.country) businessReg.incorporationCountry = contractorData.country.trim();
    if (Object.keys(businessReg).length > 0) {
      subcontractingData.businessRegistration = businessReg;
    }
    
    // Contact Information
    const primaryContact = {};
    if (contractorData.contactPerson) primaryContact.name = contractorData.contactPerson.trim();
    if (emailValue) primaryContact.email = emailValue;
    if (phoneValue) {
      primaryContact.phone = phoneValue;
      primaryContact.mobile = phoneValue;
    }
    if (Object.keys(primaryContact).length > 0) {
      subcontractingData.primaryContact = primaryContact;
    }
    
    // Addresses
    const addresses = {};
    const hqAddress = {};
    if (street) hqAddress.street = street;
    if (contractorData.city) hqAddress.city = contractorData.city.trim();
    if (contractorData.state) hqAddress.state = contractorData.state.trim();
    if (contractorData.zipCode) hqAddress.postalCode = contractorData.zipCode.trim();
    if (contractorData.country) hqAddress.country = contractorData.country.trim();
    if (Object.keys(hqAddress).length > 0) {
      addresses.headquarters = hqAddress;
      addresses.billing = { ...hqAddress };
      if (contractorData.billingAddress) {
        addresses.billing.street = contractorData.billingAddress.trim();
      }
      addresses.shipping = { ...hqAddress };
    }
    if (Object.keys(addresses).length > 0) {
      subcontractingData.addresses = addresses;
    }
    
    // Communication
    const communication = {};
    if (websiteValue) communication.website = websiteValue;
    if (emailValue) communication.email = emailValue;
    if (phoneValue) communication.phone = phoneValue;
    if (contractorData.preferredCommunication) {
      communication.preferredMethod = communicationMap[contractorData.preferredCommunication] || 'Email';
    }
    if (Object.keys(communication).length > 0) {
      subcontractingData.communication = communication;
    }
    
    // Business Details - build complete object with all fields including rates
    const businessDetails = {
      creditRating: 'Not Rated',
      paymentTerms: paymentTermsMap[contractorData.paymentTerms] || contractorData.paymentTerms || 'Net 30',
      currency: contractorData.currency || 'USD',
      taxExempt: false
    };
    
    // Process hourly rate - always include if provided, set to null if empty to clear
    if (contractorData.hourlyRate !== undefined && contractorData.hourlyRate !== null && contractorData.hourlyRate !== '') {
      const hourlyRateStr = String(contractorData.hourlyRate).trim();
      if (hourlyRateStr !== '') {
        const hourlyRate = parseFloat(hourlyRateStr);
        if (!isNaN(hourlyRate) && hourlyRate >= 0) {
          businessDetails.hourlyRate = hourlyRate;
        }
      } else {
        // Empty string after trim - clear the field
        businessDetails.hourlyRate = null;
      }
    } else if (contractorData.hourlyRate === '' || contractorData.hourlyRate === null) {
      // Explicitly empty - clear the field
      businessDetails.hourlyRate = null;
    }
    // If undefined, don't include it (preserve existing value on update)
    
    // Process daily rate - always include if provided, set to null if empty to clear
    if (contractorData.dailyRate !== undefined && contractorData.dailyRate !== null && contractorData.dailyRate !== '') {
      const dailyRateStr = String(contractorData.dailyRate).trim();
      if (dailyRateStr !== '') {
        const dailyRate = parseFloat(dailyRateStr);
        if (!isNaN(dailyRate) && dailyRate >= 0) {
          businessDetails.dailyRate = dailyRate;
        }
      } else {
        // Empty string after trim - clear the field
        businessDetails.dailyRate = null;
      }
    } else if (contractorData.dailyRate === '' || contractorData.dailyRate === null) {
      // Explicitly empty - clear the field
      businessDetails.dailyRate = null;
    }
    // If undefined, don't include it (preserve existing value on update)
    
    subcontractingData.businessDetails = businessDetails;
    
    // Banking Information - always include if bankDetails exists (even if empty to allow clearing)
    if (contractorData.bankDetails) {
      const banking = {};
      // Include all fields, even if empty, to allow updates
      banking.bankName = contractorData.bankDetails.bankName ? contractorData.bankDetails.bankName.trim() : '';
      banking.accountNumber = contractorData.bankDetails.accountNumber ? contractorData.bankDetails.accountNumber.trim() : '';
      banking.routingNumber = contractorData.bankDetails.routingNumber ? contractorData.bankDetails.routingNumber.trim() : '';
      banking.swiftCode = contractorData.bankDetails.swiftCode ? contractorData.bankDetails.swiftCode.trim() : '';
      subcontractingData.banking = banking;
    }
    
    // Compliance and Certifications
    const compliance = {
      complianceStatus: 'Under Review'
    };
    
    if (certifications.length > 0) {
      compliance.certifications = certifications;
    }
    
    if (contractorData.insuranceDetails) {
      const insurance = {};
      if (contractorData.insuranceDetails.generalLiability) {
        insurance.policyNumber = contractorData.insuranceDetails.generalLiability.trim();
      }
      if (contractorData.insuranceDetails.expirationDate) {
        insurance.expiryDate = new Date(contractorData.insuranceDetails.expirationDate);
      }
      if (contractorData.insuranceDetails.generalLiability) {
        const coverage = parseFloat(contractorData.insuranceDetails.generalLiability);
        if (!isNaN(coverage)) {
          insurance.coverageAmount = coverage;
        }
      }
      if (Object.keys(insurance).length > 0) {
        compliance.insurance = insurance;
      }
    }
    
    subcontractingData.compliance = compliance;
    
    // Performance and Ratings
    subcontractingData.performance = {
      overallRating: 3,
      qualityRating: 3,
      deliveryRating: 3,
      serviceRating: 3
    };
    if (contractorData.description) {
      subcontractingData.performance.notes = contractorData.description.trim();
    }
    
    // Service Categories
    if (serviceCategories.length > 0) {
      subcontractingData.serviceCategories = serviceCategories;
    }
    
    // Risk Assessment
    subcontractingData.riskAssessment = {
      riskLevel: 'Medium',
      riskFactors: []
    };
    
    // Portal Access
    subcontractingData.portalAccess = {
      hasAccess: false,
      accessLevel: 'Basic'
    };
    
    // Store notification settings and preferences in metadata
    // We'll use a metadata field to store UI-specific settings
    if (contractorData.notificationSettings || contractorData.preferredCommunication || contractorData.availability) {
      subcontractingData.metadata = subcontractingData.metadata || {};
      if (contractorData.notificationSettings) {
        subcontractingData.metadata.notificationSettings = {
          emailNotifications: contractorData.notificationSettings.emailNotifications !== undefined ? contractorData.notificationSettings.emailNotifications : true,
          smsNotifications: contractorData.notificationSettings.smsNotifications !== undefined ? contractorData.notificationSettings.smsNotifications : false,
          projectUpdates: contractorData.notificationSettings.projectUpdates !== undefined ? contractorData.notificationSettings.projectUpdates : true,
          paymentNotifications: contractorData.notificationSettings.paymentNotifications !== undefined ? contractorData.notificationSettings.paymentNotifications : true
        };
      }
      if (contractorData.preferredCommunication) {
        subcontractingData.metadata.preferredCommunication = contractorData.preferredCommunication;
      }
      if (contractorData.availability) {
        subcontractingData.metadata.availability = contractorData.availability;
      }
      if (contractorData.workingHours) {
        subcontractingData.metadata.workingHours = contractorData.workingHours.trim();
      }
      if (contractorData.travelWillingness) {
        subcontractingData.metadata.travelWillingness = contractorData.travelWillingness;
      }
      if (contractorData.remoteWork) {
        subcontractingData.metadata.remoteWork = contractorData.remoteWork;
      }
      if (contractorData.autoApproval !== undefined) {
        subcontractingData.metadata.autoApproval = contractorData.autoApproval;
      }
    }
    
    // Status
    subcontractingData.status = 'Active';
    subcontractingData.isActive = true;

    return subcontractingData;
  }

  // Create new subcontracting record
  static async createSubcontracting(subcontractingData) {
    try {
      console.log('Creating subcontracting with businessDetails:', JSON.stringify(subcontractingData.businessDetails, null, 2));
      const response = await api.post('/subcontracting', subcontractingData);
      return response.data;
    } catch (error) {
      console.error('Error creating subcontracting record:', error);
      throw this.handleError(error);
    }
  }

  // Update subcontracting record
  static async updateSubcontracting(id, subcontractingData) {
    try {
      console.log('Updating subcontracting with businessDetails:', JSON.stringify(subcontractingData.businessDetails, null, 2));
      const response = await api.put(`/subcontracting/${id}`, subcontractingData);
      console.log('Update response:', JSON.stringify(response.data, null, 2));
      return response.data;
    } catch (error) {
      console.error('Error updating subcontracting record:', error);
      throw this.handleError(error);
    }
  }

  // Delete subcontracting record
  static async deleteSubcontracting(id) {
    try {
      const response = await api.delete(`/subcontracting/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting subcontracting record:', error);
      throw this.handleError(error);
    }
  }

  // Handle API errors
  static handleError(error) {
    if (error.response) {
      // Server responded with error status
      const errorData = error.response.data;
      const errorMessage = errorData?.message || 
                          errorData?.error || 
                          (errorData?.errors && errorData.errors.length > 0 
                            ? errorData.errors.map(e => e.msg || e.message).join(', ') 
                            : 'An error occurred');
      
      return {
        message: errorMessage,
        status: error.response.status,
        data: errorData
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error - please check your connection',
        status: 0
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0
      };
    }
  }
}

export default SubcontractingService;

